import { clamp, preventDefault, isMobileBreakpoint } from './utilities.js';

const MIN_ZOOM = 1;
const MAX_ZOOM = 5;
const DEFAULT_MOBILE_ZOOM = 1.5;
const ZOOM_STEP = 0.5;
const WHEEL_ZOOM_STEP = 0.15;

export class DragZoomWrapper extends HTMLElement {
  #controller = new AbortController();
  /** @type {number} */
  #scale = MIN_ZOOM;
  /** @type {number} */
  #initialDistance = 0;
  /** @type {number} */
  #startScale = MIN_ZOOM;
  /** @type {Point} */
  #translate = { x: 0, y: 0 };
  /** @type {Point} */
  #startPosition = { x: 0, y: 0 };
  /** @type {Point} */
  #startTranslate = { x: 0, y: 0 };
  /** @type {boolean} */
  #isDragging = false;
  /** @type {boolean} */
  #initialized = false;
  /** @type {number | null} */
  #animationFrame = null;
  /** @type {number | null} */
  #pointerId = null;

  get #image() {
    return this.querySelector('img');
  }

  /** @returns {number} */
  get scale() {
    return this.#scale;
  }

  /** @returns {number} */
  get minScale() {
    return this.#getMinZoom() ?? MIN_ZOOM;
  }

  /** @returns {number} */
  get maxScale() {
    return MAX_ZOOM;
  }

  connectedCallback() {
    if (!this.#image) return;

    this.#scale = isMobileBreakpoint() ? DEFAULT_MOBILE_ZOOM : MIN_ZOOM;
    this.#initResizeListener();
    this.#initEventListeners();
    this.#updateTransform();
  }

  #initResizeListener() {
    this.#resizeObserver.observe(this);
  }

  #initEventListeners() {
    if (this.#initialized) return;
    this.#initialized = true;
    const { signal } = this.#controller;
    const options = { passive: false, signal };

    this.addEventListener('touchstart', this.#handleTouchStart, options);
    this.addEventListener('touchmove', this.#handleTouchMove, options);
    this.addEventListener('touchend', this.#handleTouchEnd, options);
    this.addEventListener('pointerdown', this.#handlePointerDown, options);
    this.addEventListener('pointermove', this.#handlePointerMove, options);
    this.addEventListener('pointerup', this.#handlePointerUp, { signal });
    this.addEventListener('pointercancel', this.#handlePointerUp, { signal });
    this.addEventListener('wheel', this.#handleWheel, options);
    this.#image?.addEventListener('load', this.#updateTransform, { signal });
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#resizeObserver.disconnect();
    this.#cancelAnimationFrame();
  }

  #handleResize = () => {
    if (!this.#initialized) {
      this.#initEventListeners();
    }
    if (this.#initialized) {
      this.#requestUpdateTransform();
      this.#emitZoomChange();
    }
  };

  #resizeObserver = new ResizeObserver(this.#handleResize);

  /**
   * Zoom in by a fixed step.
   * @param {number} [step]
   */
  zoomIn(step = ZOOM_STEP) {
    this.#setScale(this.#scale + step);
  }

  /**
   * Zoom out by a fixed step.
   * @param {number} [step]
   */
  zoomOut(step = ZOOM_STEP) {
    this.#setScale(this.#scale - step);
  }

  /**
   * Reset zoom to the minimum fit scale.
   */
  resetZoom() {
    this.#translate = { x: 0, y: 0 };
    this.#setScale(this.minScale);
  }

  /**
   * @param {number} nextScale
   */
  #setScale(nextScale) {
    const minZoom = this.minScale;
    this.#scale = clamp(nextScale, minZoom, MAX_ZOOM);
    if (this.#scale <= minZoom + 0.01) {
      this.#translate = { x: 0, y: 0 };
    }
    this.#requestUpdateTransform();
    this.#emitZoomChange();
    this.#syncCursor();
  }

  #emitZoomChange() {
    this.dispatchEvent(
      new CustomEvent('drag-zoom:change', {
        bubbles: true,
        detail: {
          scale: this.#scale,
          minScale: this.minScale,
          maxScale: MAX_ZOOM,
        },
      })
    );
  }

  #syncCursor() {
    const canPan = this.#scale > this.minScale + 0.01;
    this.style.cursor = canPan ? (this.#isDragging ? 'grabbing' : 'grab') : 'default';
    // At fit scale, allow vertical gallery scroll/snap; when magnified, capture gestures for pan
    this.style.touchAction = canPan ? 'none' : 'pan-y';
  }

  /**
   * @param {TouchEvent} event
   */
  #handleTouchStart = (event) => {
    preventDefault(event);

    if (event.touches.length === 2) {
      const [point1, point2] = Array.from(event.touches).map(touchToPoint);
      if (!point1 || !point2) return;
      this.#startZoomGesture(point1, point2);
    } else if (event.touches.length === 1) {
      const point = touchToPoint(event.touches[0]);
      if (!point) return;

      this.#startDragGesture(point);
    }
  };

  /**
   * @param {Point} point1
   * @param {Point} point2
   */
  #startZoomGesture(point1, point2) {
    this.#initialDistance = getDistance(point1, point2);
    this.#startScale = this.#scale;
    this.#isDragging = false;
  }

  /**
   * @param {Point} point
   */
  #startDragGesture(point) {
    this.#startPosition = { x: point.x, y: point.y };
    this.#startTranslate = { x: this.#translate.x, y: this.#translate.y };
    this.#isDragging = true;
    this.#syncCursor();
  }

  /**
   * @param {TouchEvent} event
   */
  #handleTouchMove = (event) => {
    preventDefault(event);

    const isZooming = event.touches.length === 2;
    const isDragging = event.touches.length === 1 && this.#isDragging;

    if (isZooming) {
      this.#processZoomGesture(event);
    } else if (isDragging) {
      this.#processDragGesture(event);
    }
  };

  /**
   * @param {TouchEvent} event
   */
  #processZoomGesture(event) {
    const [point1, point2] = Array.from(event.touches).map(touchToPoint);
    if (!point1 || !point2) return;

    const currentMidpoint = getMidpoint(point1, point2);
    const currentDistance = getDistance(point1, point2);
    const oldScale = this.#scale;

    const newScale = (currentDistance / this.#initialDistance) * this.#startScale;
    this.#scale = clamp(newScale, MIN_ZOOM, MAX_ZOOM);

    const containerCenter = {
      x: this.clientWidth / 2,
      y: this.clientHeight / 2,
    };

    const distanceFromCenter = {
      x: currentMidpoint.x - containerCenter.x,
      y: currentMidpoint.y - containerCenter.y,
    };

    const scaleDelta = this.#scale / oldScale - 1.0;

    this.#translate.x -= (distanceFromCenter.x * scaleDelta) / this.#scale;
    this.#translate.y -= (distanceFromCenter.y * scaleDelta) / this.#scale;

    this.#requestUpdateTransform();
    this.#isDragging = false;
    this.#emitZoomChange();
  }

  /**
   * @param {TouchEvent} event
   */
  #processDragGesture(event) {
    const point = touchToPoint(event.touches[0]);
    if (!point) return;

    this.#translate = {
      x: this.#startTranslate.x + (point.x - this.#startPosition.x) / this.#scale,
      y: this.#startTranslate.y + (point.y - this.#startPosition.y) / this.#scale,
    };

    this.#requestUpdateTransform();
  }

  /**
   * @param {TouchEvent} event
   */
  #handleTouchEnd = (event) => {
    if (event.touches.length === 0) {
      this.#isDragging = false;
      this.#requestUpdateTransform();
      this.#syncCursor();
      this.#emitZoomChange();
    }
  };

  /**
   * @param {PointerEvent} event
   */
  #handlePointerDown = (event) => {
    if (event.pointerType === 'touch') return;
    if (event.button !== 0) return;
    if (this.#scale <= this.minScale + 0.01) return;

    this.#pointerId = event.pointerId;
    this.setPointerCapture(event.pointerId);
    this.#startDragGesture({ x: event.clientX, y: event.clientY });
  };

  /**
   * @param {PointerEvent} event
   */
  #handlePointerMove = (event) => {
    if (event.pointerType === 'touch') return;
    if (!this.#isDragging || this.#pointerId !== event.pointerId) return;

    this.#translate = {
      x: this.#startTranslate.x + (event.clientX - this.#startPosition.x) / this.#scale,
      y: this.#startTranslate.y + (event.clientY - this.#startPosition.y) / this.#scale,
    };

    this.#requestUpdateTransform();
  };

  /**
   * @param {PointerEvent} event
   */
  #handlePointerUp = (event) => {
    if (event.pointerType === 'touch') return;
    if (this.#pointerId !== event.pointerId) return;

    this.#pointerId = null;
    this.#isDragging = false;
    this.#requestUpdateTransform();
    this.#syncCursor();
  };

  /**
   * @param {WheelEvent} event
   */
  #handleWheel = (event) => {
    const atMin = this.#scale <= this.minScale + 0.01;
    const atMax = this.#scale >= MAX_ZOOM - 0.01;

    // Trackpad/mouse scroll must move between snapped gallery slides.
    // Only pinch (ctrl+wheel) or an already-magnified image uses wheel to zoom —
    // otherwise scroll-up was slightly zooming the focused image.
    if (!event.ctrlKey && atMin) return;

    const zoomingIn = event.deltaY < 0;
    if (zoomingIn && atMax) return;
    if (!zoomingIn && atMin) return;

    preventDefault(event);

    if (zoomingIn) {
      this.zoomIn(WHEEL_ZOOM_STEP);
    } else {
      this.zoomOut(WHEEL_ZOOM_STEP);
    }
  };

  /**
   * @returns {number | null}
   */
  #getMinZoom = () => {
    // Desktop lightbox: baseline is fitted image (scale 1), matching global +/- controls
    if (!isMobileBreakpoint()) return MIN_ZOOM;

    const containerWidth = this.clientWidth;
    const containerHeight = this.clientHeight;
    const imageDimensions = this.#getImageDimensions();
    if (!imageDimensions || !containerWidth || !containerHeight) return null;
    const { width: imageWidth, height: imageHeight } = imageDimensions;

    // Mobile: cover the container so pinch-zoom never shows empty edges
    return Math.max(containerWidth / imageWidth, containerHeight / imageHeight) + 0.001;
  };

  /**
   * @returns {{ width: number, height: number } | null}
   */
  #getImageDimensions = () => {
    const containerRect = this.getBoundingClientRect();
    if (!this.#image) return null;
    const { naturalWidth, naturalHeight } = this.#image;
    const containerWidth = this.clientWidth;
    const containerHeight = this.clientHeight;

    if (!naturalWidth || !naturalHeight || !containerWidth || !containerHeight) return null;

    const containerAspect = containerRect.width / containerRect.height;
    const naturalAspect = naturalWidth / naturalHeight;

    let imageWidth, imageHeight;
    if (naturalAspect > containerAspect) {
      imageWidth = containerRect.width;
      imageHeight = imageWidth / naturalAspect;
    } else {
      imageHeight = containerRect.height;
      imageWidth = imageHeight * naturalAspect;
    }

    if (imageWidth === 0 || imageHeight === 0) return null;

    return { width: imageWidth, height: imageHeight };
  };

  #constrainTranslation() {
    const containerWidth = this.clientWidth;
    const containerHeight = this.clientHeight;
    if (!containerWidth || !containerHeight) return;

    const minZoom = this.#getMinZoom();
    if (!minZoom) return;

    this.#scale = clamp(this.#scale, minZoom, MAX_ZOOM);

    const imageDimensions = this.#getImageDimensions();
    if (!imageDimensions) return;

    const { width: imageWidth, height: imageHeight } = imageDimensions;

    const scaledWidth = imageWidth * this.#scale;
    const scaledHeight = imageHeight * this.#scale;

    const excessWidth = Math.max(0, scaledWidth - containerWidth);
    const excessHeight = Math.max(0, scaledHeight - containerHeight);

    const maxTranslateX = excessWidth / (2 * this.#scale);
    const maxTranslateY = excessHeight / (2 * this.#scale);

    this.#translate.x = clamp(this.#translate.x, -maxTranslateX, maxTranslateX);
    this.#translate.y = clamp(this.#translate.y, -maxTranslateY, maxTranslateY);
  }

  #requestUpdateTransform = () => {
    if (!this.#animationFrame) {
      this.#animationFrame = requestAnimationFrame(this.#updateTransform);
    }
  };

  #cancelAnimationFrame() {
    if (this.#animationFrame) {
      cancelAnimationFrame(this.#animationFrame);
      this.#animationFrame = null;
    }
  }

  #updateTransform = () => {
    this.#animationFrame = null;
    this.#constrainTranslation();
    this.style.setProperty('--drag-zoom-scale', this.#scale.toString());
    this.style.setProperty('--drag-zoom-translate-x', `${this.#translate.x}px`);
    this.style.setProperty('--drag-zoom-translate-y', `${this.#translate.y}px`);
    this.#syncCursor();
  };

  destroy() {
    this.#controller.abort();
    this.#cancelAnimationFrame();
  }
}

if (!customElements.get('drag-zoom-wrapper')) {
  customElements.define('drag-zoom-wrapper', DragZoomWrapper);
}

/**
 * @typedef {Object} Point
 * @property {number} x
 * @property {number} y
 */

/**
 * @param {Touch | undefined} touch
 * @returns {Point | undefined}
 */
function touchToPoint(touch) {
  if (!touch) return undefined;
  return { x: touch.clientX, y: touch.clientY };
}

/**
 * @param {Point} point1
 * @param {Point} point2
 * @returns {number}
 */
function getDistance(point1, point2) {
  const dx = point1.x - point2.x;
  const dy = point1.y - point2.y;
  return Math.sqrt(dx * dx + dy * dy);
}

/**
 * @param {Point} point1
 * @param {Point} point2
 * @returns {Point}
 */
function getMidpoint(point1, point2) {
  return {
    x: (point1.x + point2.x) / 2,
    y: (point1.y + point2.y) / 2,
  };
}
